import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Drawer,
  Button,
  Space,
  Tag,
  notification,
  Popconfirm,
  Spin,
  Typography,
  Alert,
} from "antd";
import {
  EnvironmentOutlined,
  SaveOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import L from "leaflet";
import "leaflet-draw";
import Api from "api";
import {
  loadBioregionGeoJSON,
  matchPolygonToRegionsAsync,
  normalizeGeoJSONCode,
  JEPSON_REGIONS,
  getRegionColor,
} from "utils/jepsonRegions";
import { isValidGeometry } from "utils/isValidGeometry";

const { Text } = Typography;

const StyledDrawer = styled(Drawer)`
  .leaflet-container {
    height: 450px;
    width: 100%;
    border-radius: 8px;
    border: 1px solid #d9d9d9;
  }
  .matched-regions {
    margin-top: 12px;
  }
  .drawer-actions {
    margin-top: 16px;
    display: flex;
    justify-content: space-between;
  }
`;

interface Props {
  isOpen: boolean;
  onClose: (action?: string) => void;
  collectionId: string;
  existingSurveyArea?: any;
  existingMatchedRegions?: string[];
}

const SurveyAreaDrawer = ({
  isOpen,
  onClose,
  collectionId,
  existingSurveyArea,
  existingMatchedRegions,
}: Props) => {
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [matchedRegions, setMatchedRegions] = useState<string[]>(
    existingMatchedRegions || []
  );
  const [drawnPolygon, setDrawnPolygon] = useState<any>(
    existingSurveyArea || null
  );
  const [mapReady, setMapReady] = useState(false);
  const [loading, setLoading] = useState(true);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const bioregionLayerRef = useRef<L.GeoJSON | null>(null);

  const initMap = useCallback(async () => {
    if (!mapContainerRef.current || mapRef.current) return;

    // Initialize map
    const map = L.map(mapContainerRef.current, {
      center: [37.5, -119.5],
      zoom: 6,
      zoomControl: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 18,
    }).addTo(map);

    mapRef.current = map;
    map.addLayer(drawnItemsRef.current);

    // Load bioregion overlay
    try {
      const features = await loadBioregionGeoJSON();
      if (features.length > 0) {
        const geoJsonData: any = {
          type: "FeatureCollection",
          features,
        };

        bioregionLayerRef.current = L.geoJSON(geoJsonData, {
          style: (feature) => {
            const code = normalizeGeoJSONCode(
              feature?.properties?.subregion || "",
              feature?.properties?.name
            );
            const color = getRegionColor(code);
            return {
              color: color,
              weight: 1.5,
              opacity: 0.7,
              fillColor: color,
              fillOpacity: 0.1,
            };
          },
          onEachFeature: (feature, layer) => {
            const code = normalizeGeoJSONCode(
              feature?.properties?.subregion || "",
              feature?.properties?.name
            );
            const region = JEPSON_REGIONS[code];
            const name = region
              ? `${region.name} (${code})`
              : feature?.properties?.name || code;
            layer.bindTooltip(name, { sticky: true });
          },
        }).addTo(map);
      }
    } catch (err) {
      console.error("Failed to load bioregion GeoJSON", err);
    }

    // Add draw controls
    const drawControl = new L.Control.Draw({
      position: "topright",
      draw: {
        polygon: {
          allowIntersection: false,
          shapeOptions: {
            color: "#1890ff",
            weight: 3,
          },
        },
        polyline: false,
        rectangle: {
          shapeOptions: {
            color: "#1890ff",
            weight: 3,
          },
        },
        circle: false,
        circlemarker: false,
        marker: false,
      },
      edit: {
        featureGroup: drawnItemsRef.current,
        remove: true,
      },
    });
    map.addControl(drawControl);

    // Handle draw events
    map.on(L.Draw.Event.CREATED, async (e: any) => {
      drawnItemsRef.current.clearLayers();
      const layer = e.layer;
      drawnItemsRef.current.addLayer(layer);

      const geoJson = layer.toGeoJSON();
      setDrawnPolygon(geoJson.geometry);

      // Match to regions
      try {
        const regions = await matchPolygonToRegionsAsync(geoJson.geometry);
        setMatchedRegions(regions);
      } catch (err) {
        console.error("Failed to match regions", err);
        setMatchedRegions([]);
      }
    });

    map.on(L.Draw.Event.DELETED, () => {
      if (drawnItemsRef.current.getLayers().length === 0) {
        setDrawnPolygon(null);
        setMatchedRegions([]);
      }
    });

    map.on(L.Draw.Event.EDITED, async () => {
      const layers = drawnItemsRef.current.getLayers();
      if (layers.length > 0) {
        const geoJson = (layers[0] as any).toGeoJSON();
        setDrawnPolygon(geoJson.geometry);
        try {
          const regions = await matchPolygonToRegionsAsync(geoJson.geometry);
          setMatchedRegions(regions);
        } catch (err) {
          console.error("Failed to match regions", err);
        }
      }
    });

    // Render existing polygon if present (guard against malformed shapes —
    // e.g. cspImport.js writes {name: "..."} which isn't valid GeoJSON).
    // Invalid shapes are treated as "no existing survey area" so the user
    // can draw a fresh one. Try/catch retained as defense-in-depth.
    if (isValidGeometry(existingSurveyArea)) {
      try {
        const existingLayer = L.geoJSON(
          { type: "Feature", geometry: existingSurveyArea, properties: {} } as any,
          {
            style: {
              color: "#1890ff",
              weight: 3,
              fillOpacity: 0.15,
            },
          }
        );
        existingLayer.eachLayer((l) => {
          drawnItemsRef.current.addLayer(l);
        });
        // Fit bounds to existing polygon
        map.fitBounds(existingLayer.getBounds(), { padding: [20, 20] });
      } catch (err) {
        console.error("Failed to render existing polygon", err);
      }
    }

    setMapReady(true);
    setLoading(false);

    // Fix Leaflet rendering after drawer animation
    setTimeout(() => {
      map.invalidateSize();
    }, 300);
  }, [existingSurveyArea]);

  useEffect(() => {
    if (isOpen) {
      // Small delay to let drawer open before initializing map
      const timer = setTimeout(() => {
        initMap();
      }, 200);
      return () => clearTimeout(timer);
    } else {
      // Cleanup map on close
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      drawnItemsRef.current = new L.FeatureGroup();
      bioregionLayerRef.current = null;
      setMapReady(false);
      setLoading(true);
    }
  }, [isOpen, initMap]);

  const handleSave = async () => {
    if (!drawnPolygon) {
      notification.warning({
        message: "No polygon drawn",
        description: "Please draw a survey area on the map first.",
      });
      return;
    }

    setSaving(true);
    try {
      await Api.put(`/collections/${collectionId}/survey-area`, {
        survey_area: drawnPolygon,
        matched_regions: matchedRegions,
      });
      notification.success({
        message: "Survey Area Saved",
        description: `Matched ${matchedRegions.length} bioregion${
          matchedRegions.length !== 1 ? "s" : ""
        }.`,
      });
      onClose("refetch");
    } catch (e: any) {
      notification.error({
        message: "Save Failed",
        description:
          e?.response?.data?.messages?.[0] || "Failed to save survey area",
      });
    }
    setSaving(false);
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      await Api.put(`/collections/${collectionId}/survey-area`, {
        survey_area: null,
        matched_regions: [],
      });
      drawnItemsRef.current.clearLayers();
      setDrawnPolygon(null);
      setMatchedRegions([]);
      notification.success({
        message: "Survey Area Cleared",
        description: "The survey area and range status column have been removed.",
      });
      onClose("refetch");
    } catch (e: any) {
      notification.error({
        message: "Clear Failed",
        description:
          e?.response?.data?.messages?.[0] || "Failed to clear survey area",
      });
    }
    setClearing(false);
  };

  return (
    <StyledDrawer
      title={
        <Space>
          <EnvironmentOutlined />
          <span>Survey Area</span>
        </Space>
      }
      placement="right"
      width={640}
      open={isOpen}
      onClose={() => onClose()}
      destroyOnClose
    >
      <Alert
        message="Draw a polygon on the map to define your survey area. Species whose bioregional distribution doesn't overlap will be flagged as out of range."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {loading && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin tip="Loading map..." />
        </div>
      )}

      <div
        ref={mapContainerRef}
        style={{ height: 450, display: loading ? "none" : "block" }}
      />

      {matchedRegions.length > 0 && (
        <div className="matched-regions">
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            Matched Bioregions ({matchedRegions.length}):
          </Text>
          <Space wrap size={[4, 4]}>
            {matchedRegions.map((code) => {
              const region = JEPSON_REGIONS[code];
              return (
                <Tag key={code} color="blue">
                  {region ? `${region.name} (${code})` : code}
                </Tag>
              );
            })}
          </Space>
        </div>
      )}

      <div className="drawer-actions">
        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!drawnPolygon}
          >
            Save Survey Area
          </Button>
          {existingSurveyArea && (
            <Popconfirm
              title="Clear survey area?"
              description="This will remove the survey area and the Range Status column."
              onConfirm={handleClear}
              okText="Clear"
              cancelText="Cancel"
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={clearing}
              >
                Clear Survey Area
              </Button>
            </Popconfirm>
          )}
        </Space>
      </div>
    </StyledDrawer>
  );
};

export default SurveyAreaDrawer;
